import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import { userAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import {
  User,
  Mail,
  Building,
  Lock,
  Eye,
  EyeOff,
  Save,
  Edit3,
  Shield,
  ShieldCheck,
} from "lucide-react";
import toast from "react-hot-toast";
import TwoFactorSetup from "../components/auth/TwoFactorSetup";

const inputClass =
  "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/70 focus:outline-none";

const Profile = () => {
  const { user, updateUser, changePassword } = useAuth();
  const { t } = useTranslation();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(
    () => user?.twoFactorEnabled ?? false,
  );

  // Keep local 2FA state in sync if the context user is updated (e.g., after re-auth)
  useEffect(() => {
    setTwoFactorEnabled(user?.twoFactorEnabled ?? false);
  }, [user?.twoFactorEnabled]);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting },
    reset: resetProfile,
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      organization: user?.organization || "",
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
    reset: resetPassword,
    setError: setPasswordError,
  } = useForm();

  const onProfileSubmit = async (data) => {
    try {
      await userAPI.updateProfile(data);
      updateUser(data);
      setIsEditingProfile(false);
      toast.success(t("profile.profileUpdated"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const onPasswordSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error(t("profile.passwordMismatch"));
      return;
    }

    const result = await changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });

    if (result.success) {
      setIsChangingPassword(false);
      resetPassword();
      toast.success(t("profile.passwordUpdated"));
    } else if (result.details && Array.isArray(result.details)) {
      result.details.forEach(({ path, msg }) => {
        if (["currentPassword", "newPassword"].includes(path)) {
          setPasswordError(path, { message: msg });
        }
      });
    } else if (result.error === "Current password is incorrect") {
      setPasswordError("currentPassword", {
        message: t("profile.passwordWrongCurrent"),
      });
    } else {
      setPasswordError("root", {
        message: result.error || t("profile.passwordChangeError"),
      });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleEditToggle = () => {
    if (isEditingProfile) {
      resetProfile();
      setIsEditingProfile(false);
    } else {
      setIsEditingProfile(true);
    }
  };

  const handlePasswordToggle = () => {
    if (isChangingPassword) {
      resetPassword();
      setIsChangingPassword(false);
    } else {
      setIsChangingPassword(true);
    }
  };

  const membershipDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-CH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-24 left-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[26rem] w-[26rem] rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.12)_1px,_transparent_0)] [background-size:34px_34px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-12 sm:px-8 lg:px-12 space-y-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white">
                {t("profile.title")}
              </h1>
              <p className="mt-2 text-sm text-slate-300 max-w-2xl">
                {t("profile.subtitle")}
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2">
                  <Mail className="h-4 w-4 text-cyan-200" />
                  {user?.email || "n/a"}
                </span>
                <span className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 font-mono text-xs">
                  ID · {user?.id || "—"}
                </span>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-slate-950/40 px-6 py-5 text-right">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("profile.memberSince")}
              </p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {membershipDate}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {user?.isEmailVerified
                  ? t("profile.verifiedIdentity")
                  : t("profile.verificationPending")}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleEditToggle}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30"
            >
              <Edit3 className="h-4 w-4" />
              {isEditingProfile
                ? t("profile.cancelEdit")
                : t("profile.editProfile")}
            </button>
            <button
              onClick={handlePasswordToggle}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
            >
              <Lock className="h-4 w-4" />
              {isChangingPassword
                ? t("common.close")
                : t("profile.changePassword")}
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[3fr,2fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-white">
                    {t("profile.profileDetails")}
                  </h2>
                </div>
              </div>

              {isEditingProfile ? (
                <form
                  onSubmit={handleProfileSubmit(onProfileSubmit)}
                  className="mt-6 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-white/80">
                        {t("profile.firstName")}
                      </label>
                      <div className="relative mt-2">
                        <input
                          {...registerProfile("firstName", {
                            required: t("profile.firstNameRequired"),
                          })}
                          type="text"
                          className={`${inputClass} pl-12`}
                          placeholder="Sofia"
                        />
                        <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      </div>
                      {profileErrors.firstName && (
                        <p className="mt-2 text-xs text-rose-300">
                          {profileErrors.firstName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-white/80">
                        {t("profile.lastName")}
                      </label>
                      <div className="relative mt-2">
                        <input
                          {...registerProfile("lastName", {
                            required: t("profile.lastNameRequired"),
                          })}
                          type="text"
                          className={`${inputClass} pl-12`}
                          placeholder="Müller"
                        />
                        <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      </div>
                      {profileErrors.lastName && (
                        <p className="mt-2 text-xs text-rose-300">
                          {profileErrors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-white/80">
                      {t("profile.organization")}
                    </label>
                    <div className="relative mt-2">
                      <input
                        {...registerProfile("organization")}
                        type="text"
                        className={`${inputClass} pl-12`}
                        placeholder="Swiss Parliament AI"
                      />
                      <Building className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={isProfileSubmitting}
                      className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {isProfileSubmitting ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {t("profile.saveProfile")}
                    </button>
                    <button
                      type="button"
                      onClick={handleEditToggle}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-5 py-3 text-sm font-semibold text-white/80"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {t("profile.firstName")}
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {user?.firstName || "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {t("profile.lastName")}
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {user?.lastName || "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {t("profile.email")}
                    </p>
                    <p className="mt-2 text-base text-white">
                      {user?.email || "—"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {t("profile.organization")}
                    </p>
                    <p className="mt-2 text-base text-white">
                      {user?.organization || t("profile.notSpecified")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-white">
                    {t("profile.accessControl")}
                  </h2>
                </div>
                <Shield className="h-5 w-5 text-cyan-200" />
              </div>

              {isChangingPassword ? (
                <form
                  onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                  className="mt-6 space-y-4"
                >
                  {[
                    {
                      label: t("profile.currentPassword"),
                      field: "currentPassword",
                      error: passwordErrors.currentPassword,
                      visibilityKey: "current",
                    },
                    {
                      label: t("profile.newPassword"),
                      field: "newPassword",
                      error: passwordErrors.newPassword,
                      visibilityKey: "new",
                      rules: {
                        minLength: {
                          value: 12,
                          message: t("profile.passwordMinLength"),
                        },
                      },
                    },
                    {
                      label: t("profile.confirmPassword"),
                      field: "confirmPassword",
                      error: passwordErrors.confirmPassword,
                      visibilityKey: "confirm",
                    },
                  ].map((config) => (
                    <div key={config.field}>
                      <label className="text-sm font-medium text-white/80">
                        {config.label}
                      </label>
                      <div className="relative mt-2">
                        <input
                          {...registerPassword(config.field, {
                            required: t("profile.fieldRequired"),
                            ...(config.rules || {}),
                          })}
                          type={
                            showPasswords[config.visibilityKey]
                              ? "text"
                              : "password"
                          }
                          className={`${inputClass} pl-12 pr-12`}
                        />
                        <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                          onClick={() =>
                            togglePasswordVisibility(config.visibilityKey)
                          }
                        >
                          {showPasswords[config.visibilityKey] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {config.error && (
                        <p className="mt-2 text-xs text-rose-300">
                          {config.error.message}
                        </p>
                      )}
                    </div>
                  ))}

                  {passwordErrors.root && (
                    <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                      {passwordErrors.root.message}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={isPasswordSubmitting}
                      className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {isPasswordSubmitting ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      ) : (
                        <ShieldCheck className="h-4 w-4" />
                      )}
                      {t("profile.updatePassword")}
                    </button>
                    <button
                      type="button"
                      onClick={handlePasswordToggle}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-5 py-3 text-sm font-semibold text-white/80"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-6 rounded-2xl border border-dashed border-white/10 bg-white/5 px-5 py-6 text-sm text-slate-300">
                  <p>{t("profile.passwordHintText")}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.4em] text-slate-400">
                    {t("profile.passwordTipLabel")}
                  </p>
                  <p className="text-xs text-slate-400">
                    {t("profile.passwordTipText")}
                  </p>
                </div>
              )}
            </div>

            {/* ─── 2FA card ─────────────────────────────────────── */}
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-white">
                    {t("twoFactor.profile.title")}
                  </h2>
                  <p className="mt-1 text-sm text-slate-400">
                    {t("twoFactor.profile.subtitle")}
                  </p>
                </div>
                <ShieldCheck className="h-5 w-5 text-cyan-200" />
              </div>
              <div className="mt-6">
                <TwoFactorSetup
                  enabled={twoFactorEnabled}
                  onStatusChange={(newStatus) => {
                    setTwoFactorEnabled(newStatus);
                    updateUser({ twoFactorEnabled: newStatus });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("profile.accountSectionLabel")}
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                {t("profile.accountIntelligence")}
              </h2>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {t("profile.accountId")}
                  </p>
                  <p className="mt-2 font-mono text-base text-white">
                    {user?.id || "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {t("profile.memberSince")}
                  </p>
                  <p className="mt-2 text-base text-white">{membershipDate}</p>
                </div>
                <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {t("profile.accountStatus")}
                  </p>
                  <span className="mt-2 inline-flex rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-200">
                    {t("profile.accountActive")}
                  </span>
                </div>
                <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {t("profile.emailVerification")}
                  </p>
                  <span
                    className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] ${
                      user?.isEmailVerified
                        ? "bg-emerald-400/10 text-emerald-200"
                        : "bg-amber-400/10 text-amber-200"
                    }`}
                  >
                    {user?.isEmailVerified
                      ? t("profile.verified")
                      : t("profile.pending")}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("profile.contactSectionLabel")}
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                {t("profile.reachAndSupport")}
              </h2>
              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <p>{t("profile.contactText1")}</p>
                <p>{t("profile.contactText2")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
